import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createAdAsync, clearError } from '../store/slices/adsSlice';
import LoadingSpinner from './LoadingSpinner';
import InfoTip from './InfoTip';
import './GroupForm.css';

const CreateAd: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.ads);

  const [formData, setFormData] = useState({
    name: '',
    dateStart: '',
    dateEnd: '',
    budget: '',
    platform: '',
    channelType: '',
    description: '',
    goal: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Ad name is required';
    }

    if (!formData.dateStart) {
      errors.dateStart = 'Start date is required';
    }

    if (!formData.dateEnd) {
      errors.dateEnd = 'End date is required';
    }

    if (formData.dateStart && formData.dateEnd) {
      if (new Date(formData.dateEnd) <= new Date(formData.dateStart)) {
        errors.dateEnd = 'End date must be after start date';
      }
    }

    if (formData.budget && (isNaN(Number(formData.budget)) || Number(formData.budget) < 0)) {
      errors.budget = 'Budget must be a positive number';
    }

    if (!formData.platform) {
      errors.platform = 'Platform is required';
    }

    if (!formData.channelType) {
      errors.channelType = 'Channel type is required';
    }

    if (!formData.goal) {
      errors.goal = 'Goal is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors
    if (error) dispatch(clearError());
    
    if (!validateForm()) {
      return;
    }

    const adData = {
      name: formData.name.trim(),
      dateStart: formData.dateStart,
      dateEnd: formData.dateEnd,
      budget: formData.budget ? parseInt(formData.budget, 10) : null,
      platform: formData.platform as 'instagram' | 'telegram',
      channelType: formData.channelType as 'native' | 'partnership',
      description: formData.description.trim() || null,
      goal: formData.goal as 'ig_followers' | 'tg_followers' | 'sales',
    };

    try {
      await dispatch(createAdAsync(adData)).unwrap();
      // Navigate back to ads list
      navigate('/ads');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/ads');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create New Ad</h1>
          <p>Add a new advertising campaign</p>
        </div>
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Ad Information</h2>
          
          <div className="form-group">
            <label htmlFor="name">Ad Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter ad name"
              className={`form-input ${formErrors.name ? 'error' : ''}`}
              required
            />
            {formErrors.name && <span className="error-text">{formErrors.name}</span>}
          </div>

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="dateStart">Start Date *</label>
              <input
                type="date"
                id="dateStart"
                name="dateStart"
                value={formData.dateStart}
                onChange={handleInputChange}
                className={`form-input ${formErrors.dateStart ? 'error' : ''}`}
                required
              />
              {formErrors.dateStart && <span className="error-text">{formErrors.dateStart}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="dateEnd">End Date *</label>
              <input
                type="date"
                id="dateEnd"
                name="dateEnd"
                value={formData.dateEnd}
                onChange={handleInputChange}
                className={`form-input ${formErrors.dateEnd ? 'error' : ''}`}
                required
              />
              {formErrors.dateEnd && <span className="error-text">{formErrors.dateEnd}</span>}
            </div>
          </div>

          <div className="form-group" style={{ maxWidth: '300px' }}>
            <div className="label-with-info">
              <label htmlFor="budget">Budget (Optional)</label>
              <InfoTip text="Leave empty if budget is not defined" />
            </div>
            <input
              type="number"
              id="budget"
              name="budget"
              value={formData.budget}
              onChange={handleInputChange}
              placeholder="Enter budget amount"
              className={`form-input ${formErrors.budget ? 'error' : ''}`}
              min="0"
              step="1"
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault();
                }
              }}
            />
            {formErrors.budget && <span className="error-text">{formErrors.budget}</span>}
          </div>

          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="platform">Platform *</label>
              <select
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleInputChange}
                className={`form-select ${formErrors.platform ? 'error' : ''}`}
                required
              >
                <option value="">Select platform</option>
                <option value="instagram">Instagram</option>
                <option value="telegram">Telegram</option>
              </select>
              {formErrors.platform && <span className="error-text">{formErrors.platform}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="channelType">Channel Type *</label>
              <select
                id="channelType"
                name="channelType"
                value={formData.channelType}
                onChange={handleInputChange}
                className={`form-select ${formErrors.channelType ? 'error' : ''}`}
                required
              >
                <option value="">Select channel type</option>
                <option value="native">Native</option>
                <option value="partnership">Partnership</option>
              </select>
              {formErrors.channelType && <span className="error-text">{formErrors.channelType}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="goal">Goal *</label>
            <select
              id="goal"
              name="goal"
              value={formData.goal}
              onChange={handleInputChange}
              className={`form-select ${formErrors.goal ? 'error' : ''}`}
              required
            >
              <option value="">Select goal</option>
              <option value="ig_followers">IG Followers</option>
              <option value="tg_followers">TG Followers</option>
              <option value="sales">Sales</option>
            </select>
            {formErrors.goal && <span className="error-text">{formErrors.goal}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter ad description"
              className="form-input"
              rows={4}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Ad'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAd;
